/* eslint-disable react-refresh/only-export-components */
import { useTranslation } from 'react-i18next'
import {
	Links,
	LinksFunction,
	LoaderFunctionArgs,
	Meta,
	MetaFunction,
	Outlet,
	Scripts,
	ScrollRestoration,
	useRouteLoaderData
} from 'react-router'

import {
	QueryClient,
	QueryClientProvider,
	isServer
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { useChangeLanguage } from 'remix-i18next/react'

import { ThemeProvider } from '@wwt/ui/theme'

import { I18N_DEFAULT_NS, I18N_FALLBACK_LANGUAGE } from '@/shared/config/i18n'
import i18next from '@/shared/i18n/i18next.server'
import { TooltipProvider } from '@/shared/ui/Tooltip'

import './main.css'
import { createQueryClient, getPersistOptions } from './query'

export const meta: MetaFunction = () => [{ title: 'WWT' }]

export const links: LinksFunction = () => [
	{ rel: 'icon', href: `/favicons-${import.meta.env.MODE}/favicon.ico` },
	{
		rel: 'icon',
		type: 'image/svg+xml',
		href: `/favicons-${import.meta.env.MODE}/favicon.svg`
	},
	// Safari
	{
		rel: 'mask-icon',
		type: 'image/svg+xml',
		href: `/favicons-${import.meta.env.MODE}/favicon.svg`,
		color: '#000000'
	},
	{
		rel: 'apple-touch-icon',
		sizes: '180x180',
		href: `/favicons-${import.meta.env.MODE}/apple-touch-icon.png`
	},
	{
		rel: 'icon',
		type: 'image/png',
		sizes: '32x32',
		href: `/favicons-${import.meta.env.MODE}/favicon-32x32.png`
	},
	{
		rel: 'icon',
		type: 'image/png',
		sizes: '16x16',
		href: `/favicons-${import.meta.env.MODE}/favicon-16x16.png`
	},
	{ rel: 'manifest', href: `/favicons-${import.meta.env.MODE}/manifest.json` }
]

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const locale = await i18next.getLocale(request)
	return { locale }
}

export const handle = {
	i18n: [
		I18N_DEFAULT_NS,

		// XXX: Fix for show errors on reservation page - FE-3717
		// TODO: Delete "validation" after change logic of how we show an
		// errors on reservation form
		'validation'
	]
}

interface LayoutProps {
	children: React.ReactNode
}

let browserQueryClient: QueryClient | undefined = undefined

const getQueryClient = () => {
	if (isServer) {
		// Server: always make a new query client
		return createQueryClient()
	} else {
		// Browser: make a new query client if we don't already have one
		// This is very important, so we don't re-make a new client if React
		// suspends during the initial render. This may not be needed if we
		// have a suspense boundary BELOW the creation of the query client
		if (!browserQueryClient) browserQueryClient = createQueryClient()

		// 	const persistOptions = getPersistOptions(browserQueryClient)

		// persistQueryClient(persistOptions)
		return browserQueryClient
	}
}

interface QueryProviderProps {
	children: React.ReactNode
}

const QueryProvider = ({ children }: QueryProviderProps) => {
	const queryClient = getQueryClient()

	if (isServer) {
		return (
			<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
		)
	}

	const persistOptions = getPersistOptions(queryClient)
	return (
		<PersistQueryClientProvider
			client={queryClient}
			persistOptions={persistOptions}
		>
			{children}
		</PersistQueryClientProvider>
	)
}

export const Layout = ({ children }: LayoutProps) => {
	const data = useRouteLoaderData<typeof loader>('root')

	const locale = data?.locale ?? I18N_FALLBACK_LANGUAGE
	const { i18n } = useTranslation()

	useChangeLanguage(locale)

	return (
		<html
			lang={locale}
			dir={i18n.dir()}
		>
			<head>
				<meta charSet="UTF-8" />
				<meta
					name="viewport"
					content="width=device-width, minimum-scale=1.0, initial-scale=1.0"
				/>
				<Meta />
				<Links />

				{/* Meta Pixel Code */}
				<script
					suppressHydrationWarning
					dangerouslySetInnerHTML={{
						__html: `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '1184546122895707');
fbq('track', 'PageView');`
					}}
				/>
				{/* End Meta Pixel Code */}
			</head>
			<body>
				<QueryProvider>
					<ThemeProvider>
						<TooltipProvider>{children}</TooltipProvider>
					</ThemeProvider>
					<ReactQueryDevtools initialIsOpen={false} />
				</QueryProvider>

				<ScrollRestoration getKey={location => location.pathname} />
				<Scripts />

				{/* Meta Pixel Code */}
				<noscript>
					{/* eslint-disable-next-line jsx-a11y/alt-text */}
					<img
						height="1"
						width="1"
						style={{ display: 'none' }}
						src="https://www.facebook.com/tr?id=1184546122895707&ev=PageView&noscript=1"
					/>
				</noscript>
				{/* End Meta Pixel Code */}
			</body>
		</html>
	)
}

const Root = () => {
	return <Outlet />
}

export default Root
